


// src/pages/Projects/ProjectList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import ProjectWizard from '../../components/ProjectWizard';

export default function ProjectList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/projects');
      setProjects(data.projects || []);
    } catch (err) {
      console.error(err);
      alert('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedProject(null);
    setPanelOpen(true);
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setPanelOpen(true);
  };

  // const handleDelete = async (id) => {
  //   if (!window.confirm('Archive this project?')) return;
  //   try {
  //     await api.delete(`/projects/${id}`);
  //     setProjects(projects.filter((p) => p.id !== id));
  //   } catch (err) {
  //     alert('Failed to archive project');
  //   }
  // };
const handleDelete = async (id) => {
  if (!window.confirm('Archive this project? It will be hidden from the list.')) return;
  try {
    await api.delete(`/projects/${id}`);
    setProjects(projects.filter((p) => p.id !== id));
    alert('Project archived successfully');
  } catch (err) {
    alert('Failed to archive project');
  }
};
  const handleSuccess = () => {
    setPanelOpen(false);
    fetchProjects();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <h1 className="text-3xl font-bold text-black dark:text-white tracking-tight">
          Projects
        </h1>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-orange-600 active:bg-orange-600 text-white font-medium rounded-lg shadow-md transition-all duration-150 transform hover:scale-[1.02]"
        >
          <PlusIcon className="w-5 h-5" />
          New Project
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500 font-medium">Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 text-gray-600 dark:text-gray-400 bg-gray-50/50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700">
          <p className="text-lg font-medium">No projects found yet.</p>
          <p className="mt-2">Create your first project to get started!</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800/60">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Project Code
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Start
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Progress
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                {projects.map((project) => (
                  <tr
                    key={project.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600 dark:text-orange-400">
                      {project.project_uid}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-black dark:text-white">
                      {project.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {project.project_type || 'Other'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {project.start_date || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex-shrink-0">
                          <div
                            className="h-full bg-orange-600 transition-all duration-500 ease-out"
                            style={{ width: `${project.overall_progress || 0}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 min-w-[3rem]">
                          {project.overall_progress || 0}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-4">
                        <button
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className="text-gray-700 hover:text-black dark:text-gray-300 dark:hover:text-white transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(project)}
                          className="text-gray-700 hover:text-orange-600 dark:text-gray-300 dark:hover:text-orange-500 transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(project.id)}
                          className="text-gray-700 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-500 transition-colors"
                          title="Delete / Archive"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ProjectWizard
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        project={selectedProject}
        onSuccess={handleSuccess}
      />
    </div>
  );
}